import React, {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import { useTranslation } from 'react-i18next';
import {Col, Row,Button,Divider, message, Checkbox, Modal, Upload, Collapse, Select} from 'antd';
import { UploadOutlined, DownloadOutlined, FileAddOutlined, DownOutlined } from '@ant-design/icons';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import * as XLSX from 'xlsx';
import { ChannelSelectFilter } from '../../../../components/settlement/ChannelSelectFilter';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import PricingItemPanel from './PriceItemPanel';
import DeliveryFeeTabb, { DeliveryFeeData, NEGATIVE_ONLY_FEE_ITEM_CODES } from './DeliveryFeeTabb'
import PickupFeeTabb, { PickupFeeData } from './PickupFeeTabb'
import RouteFeeTabb, { RouteFeeData } from './RouteFeeTabb'
import WeightOrVolumeWeightTabb from './WeightOrVolumeWeightTabb'
import LengthOrWidthOrHeightTabb from './LengthOrWidthOrHeightTabb';
import BizTypeFeeTabb, { BizTypeFeeData } from './BizTypeFeeTabb';
import FuelSurchargeRateTabb, { FUEL_SURCHARGE_FEE_ITEM_CODE, validateFuelSurchargeRate } from './FuelSurchargeRateTabb';
import { NEW_PRICING_SCHEME, type PricingSchemeInputData, type NewPricingSchemeResponse } from 'graphql/mutations/settlement/newPricingScheme';
import { UPDATE_PRICING_SCHEME, type UpdatePricingSchemeResponse } from 'graphql/mutations/settlement/updatePricingScheme';
import { GET_PRICING_FEE_ITEMS_BY_SCENARIO, GET_PRICING_FEE_ITEM_FACTORS, GET_PRICING_FACTORS_GROUPED_BY_PARENT_CODE, BUSINESS_SCENARIO_ORDER, FEE_ITEM_SCENARIO_FILTER_OPTIONS } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { GET_PRICING_SCHEME_DETAIL, type PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';
import { PricingFormContext } from './PricingFormContext';
import {ResourceCode} from "../../../../constants/resourceCodes";
import {HasResource} from "../../../../components/HasResource";

interface CreateSchemeCompProps {
    businessScenario: number | null;
    onSaveSuccess?: () => void;
    viewMode?: 'create' | 'view' | 'edit';
    pricingSchemeId?: number | null;
    /** 复制时预填的方案 ID，仅 viewMode='create' 时使用，会拉取该方案详情并填充表单 */
    copyFromSchemeId?: number | null;
}

const CreateSchemeComp: React.FC<CreateSchemeCompProps> = ({ businessScenario, onSaveSuccess, viewMode = 'create', pricingSchemeId = null, copyFromSchemeId = null }) => {
    const { t } = useTranslation();
    const isViewMode = viewMode === 'view';
    const isEditMode = viewMode === 'edit';
    const [templateSectionVisible, setTemplateSectionVisible] = useState(false);

    // businessScenario 是通过 GraphQL Long 标量传下来的，Apollo 常把 Long 序列化成字符串（如 "1001001"）
    // 而不是 number，导致下面与 BUSINESS_SCENARIO_ORDER 等 number 常量做 === 严格比较时恒为 false
    // （"费用项范围"选择框、模版上传Row 都因此曾经不显示）。这里统一转换成 number 后再比较。
    const businessScenarioNum = businessScenario != null ? Number(businessScenario) : null;

    // TT 退件费：在当前页面上展示为“退件费（TT）”，并复用配送费同款展示/校验逻辑
    // 注意：业务/因子结构由后端决定，前端仅做展示名称映射，避免影响保存校验分支。
    const TT_RETURN_FEE_CODE = 1001001059;
    // 基础运费（配送费），燃油附加费按它计算
    const BASE_PRICING_FEE_ITEM_CODE = 1001001001;
    const getFeeItemDisplayName = (feeItem: any): string => {
        const code = feeItem?.businessFeeCode;
        const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
        if (codeNum === TT_RETURN_FEE_CODE) return '退件费（TT）';
        return feeItem?.businessFeeName ?? '';
    };
    // 基础信息
    const [clientId, setClientId] = useState<number | null>(null);
    const [clientName, setClientName] = useState<string>('');
    const [clientIdString, setClientIdString] = useState<string | undefined>(undefined);
    const [pricingSchemeName, setPricingSchemeName] = useState<string>('');
    const [pricingSchemeDescription, setPricingSchemeDescription] = useState<string>('');

    // 获取费用项列表（根据业务场景）。业务场景为"客户计费"（BUSINESS_SCENARIO_ORDER，1001001）时，
    // 后端 getPricingFeeItemsByBusinessScenarioCode 会一并返回收入侧分拣段计费场景（1001004，
    // 即TT取件费/TT分拣费/TT卸车费）的费用项，前端无需为此额外发起第二次查询，
    // 只需按下方"费用项范围"二级选择框对已返回的结果做展示筛选即可（见 visibleFeeItems）。
    const { data: feeItemData, loading: feeItemLoading } = useQuery(GET_PRICING_FEE_ITEMS_BY_SCENARIO, {
        variables: { businessScenarioCode: businessScenario || 0 },
        skip: !businessScenario,
    });

    // "费用项范围"二级选择框当前选中的场景码，仅在 businessScenario === BUSINESS_SCENARIO_ORDER 时展示/生效
    const [feeItemScenarioFilter, setFeeItemScenarioFilter] = useState<number>(BUSINESS_SCENARIO_ORDER);

    // 获取按父计费因子code分组的计费因子列表
    const { data: pricingFactorsGroupedData } = useQuery(GET_PRICING_FACTORS_GROUPED_BY_PARENT_CODE);

    // 获取定价方案详情（查看/编辑模式用 pricingSchemeId，复制模式用 copyFromSchemeId）
    const detailSchemeId = (viewMode === 'view' || viewMode === 'edit') ? pricingSchemeId : copyFromSchemeId;
    const { data: schemeDetailData, loading: schemeDetailLoading } = useQuery<PricingSchemeDetailResponse>(
        GET_PRICING_SCHEME_DETAIL,
        {
            variables: { pricingSchemeId: detailSchemeId! },
            skip: !detailSchemeId,
            fetchPolicy: 'network-only'
        }
    );
    useEffect(() => {
        const detail = schemeDetailData?.getPricingSchemeDetail;
        if (detail?.messageKey || detail?.message) {
            message.error(resolvePricingSchemeMessage(detail, '加载定价方案详情失败'));
        }
    }, [schemeDetailData]);

    // 费用项列表（"客户计费"下这里已经是后端合并好的1001001+1001004两个场景的费用项，
    // 其它业务场景仍只有该场景自己的费用项，行为不变）
    const feeItems = useMemo(() => {
        return feeItemData?.getPricingFeeItemsByBusinessScenarioCode ?? [];
    }, [feeItemData]);

    // 用于Tab展示的费用项子集："客户计费"下按二级选择框（收入侧尾程1001001 / 收入侧分拣1001004）筛选，
    // 只影响页面上展示哪些Tab，不影响 feeItems 本身（选中状态、计费因子查询、保存校验等仍作用于全量费用项）
    const visibleFeeItems = useMemo(() => {
        if (businessScenarioNum !== BUSINESS_SCENARIO_ORDER) {
            return feeItems;
        }
        return feeItems.filter((feeItem: any) => Number(feeItem.businessScenarioCode) === feeItemScenarioFilter);
    }, [feeItems, businessScenarioNum, feeItemScenarioFilter]);

    // 计费因子选项映射（按 parent_factor_code 分组）
    const pricingFactorOptionsMap = useMemo(() => {
        const map = new Map<string, Array<{ label: string; value: string }>>();
        if (pricingFactorsGroupedData?.getPricingFactorsGroupedByParentCode) {
            pricingFactorsGroupedData.getPricingFactorsGroupedByParentCode.forEach((group: any) => {
                const options = group.factors.map((factor: any) => ({
                    label: factor.factorName,
                    value: factor.factorName // 使用 factorName 作为 value，用于显示
                }));
                map.set(group.parentFactorCode, options);
            });
        }
        return map;
    }, [pricingFactorsGroupedData]);

    // factor_name 到 factor_value 的映射（用于保存时转换）
    const factorNameToValueMap = useMemo(() => {
        const map = new Map<string, string>();
        if (pricingFactorsGroupedData?.getPricingFactorsGroupedByParentCode) {
            pricingFactorsGroupedData.getPricingFactorsGroupedByParentCode.forEach((group: any) => {
                group.factors.forEach((factor: any) => {
                    map.set(factor.factorName, factor.factorValue);
                });
            });
        }
        return map;
    }, [pricingFactorsGroupedData]);

    // factor_value 到 factor_name 的映射（用于编辑时转换）
    const factorValueToNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if (pricingFactorsGroupedData?.getPricingFactorsGroupedByParentCode) {
            pricingFactorsGroupedData.getPricingFactorsGroupedByParentCode.forEach((group: any) => {
                group.factors.forEach((factor: any) => {
                    map.set(factor.factorValue, factor.factorName);
                });
            });
        }
        return map;
    }, [pricingFactorsGroupedData]);

    // 费用项计费因子映射 - 存储每个费用项的计费因子列表
    const [feeItemFactorsMap, setFeeItemFactorsMap] = useState<Map<number, string[]>>(new Map());
    // 计费因子更新计数器，用于强制触发 useMemo 重新计算
    const [factorsUpdateKey, setFactorsUpdateKey] = useState(0);
    // 已查询的费用项集合，使用 ref 避免触发重新渲染
    const queriedFeeItemCodesRef = useRef<Set<number>>(new Set());
    // 选中的费用项集合 - 存储被勾选的费用项 code
    const [selectedFeeItemCodes, setSelectedFeeItemCodes] = useState<Set<number>>(new Set());
    
    // 使用 useMutation hook 来确保请求在 network 中可见
    const [submitNewPricingScheme, { loading: savingNew }] = useMutation<NewPricingSchemeResponse, { input: PricingSchemeInputData }>(
        NEW_PRICING_SCHEME,
        {
            fetchPolicy: 'network-only', // 确保从网络请求，不在缓存中
            errorPolicy: 'all' // 即使有错误也返回部分数据
        }
    );

    const [updatePricingScheme, { loading: savingUpdate }] = useMutation<UpdatePricingSchemeResponse, { input: PricingSchemeInputData }>(
        UPDATE_PRICING_SCHEME,
        {
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        }
    );

    const saving = savingNew || savingUpdate;
    
    // 查询计费因子的函数 - 使用 network-only 避免缓存问题
    const [getFeeItemFactors] = useLazyQuery(GET_PRICING_FEE_ITEM_FACTORS, {
        fetchPolicy: 'network-only'
    });

    // 当费用项列表变化时，并行查询所有费用项的计费因子，最后一次性更新 state，避免 N 次 setState 导致 N 次整树重渲染
    useEffect(() => {
        if (!feeItems || feeItems.length === 0) return;
        const toQuery = feeItems
            .map((feeItem: any) => {
                const code = feeItem.businessFeeCode;
                const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
                return { codeNum, code };
            })
            .filter(({ codeNum }: { codeNum: number }) => !queriedFeeItemCodesRef.current.has(codeNum));
        if (toQuery.length === 0) return;
        toQuery.forEach(({ codeNum }: { codeNum: number }) => queriedFeeItemCodesRef.current.add(codeNum));

        const run = async () => {
            const results = await Promise.all(
                toQuery.map(async ({ codeNum }: { codeNum: number }) => {
                    try {
                        const result = await getFeeItemFactors({
                            variables: { feeItemCode: codeNum },
                            fetchPolicy: 'network-only',
                        });
                        const factors = result.data?.getPricingFeeItemFactorsByFeeItemCode
                            ?.map((factor: any) => factor?.pricingFactor)
                            ?.filter((f: string | null) => f != null && f !== '') as string[] | undefined;
                        return { codeNum, factors: factors && factors.length > 0 ? factors : null };
                    } catch (e: any) {
                        console.error(`[CreateSchemeComp] 查询费用项 ${codeNum} 的计费因子失败:`, e);
                        return { codeNum, factors: null };
                    }
                })
            );
            const nextMap = new Map<number, string[]>();
            results.forEach(({ codeNum, factors }) => {
                if (factors) nextMap.set(codeNum, factors);
            });
            if (nextMap.size > 0) {
                setFeeItemFactorsMap(prev => {
                    const m = new Map(prev);
                    nextMap.forEach((v, k) => m.set(k, v));
                    return m;
                });
                setFactorsUpdateKey(k => k + 1);
            }
        };
        run();
    }, [feeItems, getFeeItemFactors]);

    // 处理客户选择变化
    const handleClientChange = (value: string | undefined) => {
        if (value) {
            // ChannelSelectFilter 返回的是 channel id（字符串），需要转换为 number
            const id = parseInt(value, 10);
            setClientIdString(value);
            setClientId(id);
        } else {
            setClientIdString(undefined);
            setClientId(null);
            setClientName('');
        }
    };

    // 处理客户选择（获取客户名称）
    const handleClientSelect = (value: string, option: { label: string; value: string }) => {
        setClientName(option.label);
    };

    // 费用项数据状态管理 - 使用 Map 来存储每个费用项的数据
    const [feeItemDataMap, setFeeItemDataMap] = useState<Map<number, any>>(new Map());

    // 稳定回调：按费用项 code 更新数据，避免每个 tab 的 onChange 引用变化导致整树重渲染
    const updateFeeItemDataByCode = useCallback((feeItemCodeNum: number, newData: any) => {
        setFeeItemDataMap(prev => {
            const next = new Map(prev);
            next.set(feeItemCodeNum, newData);
            return next;
        });
    }, []);

    // 加载定价方案详情数据（查看/编辑模式，或复制模式预填）
    useEffect(() => {
        const shouldFillFromDetail = (viewMode === 'view' || viewMode === 'edit' || (viewMode === 'create' && copyFromSchemeId));
        if (shouldFillFromDetail && schemeDetailData?.getPricingSchemeDetail && feeItems.length > 0 && factorValueToNameMap.size > 0) {
            const detail = schemeDetailData.getPricingSchemeDetail;
            
            // 设置基础信息
            setClientId(detail.clientId);
            setClientIdString(detail.clientId?.toString());
            setPricingSchemeName(detail.pricingSchemeName);
            setPricingSchemeDescription(detail.pricingSchemeDescription || '');
            setClientName(detail.clientName || '');
            
            // 为每个费用项还原数据
            const newFeeItemDataMap = new Map<number, any>();
            const selectedFeeItemCodesSet = new Set<number>();
            
            // 遍历 priceConfig，只处理配置了的费用项
            detail.priceConfig.forEach((priceConfig: any) => {
                const feeItemCode = priceConfig.pricingFeeItemCode;
                const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
                
                // 获取该费用项的计费因子
                const factors = feeItemFactorsMap.get(feeItemCodeNum) || [];
                const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
                const hasLine = factors.some((f: string) => f && f.toLowerCase() === 'line');
                const hasStartWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight');
                const hasEndWeight = factors.some((f: string) => f && f.toLowerCase() === 'endweight');
                const hasWeight = hasStartWeight && hasEndWeight;
                const hasWeightOrVolumeWeight = factors.some((f: string) => f && f.toLowerCase() === 'weightorvolumeweight');
                const hasLengthOrWidthOrHeight = factors.some((f: string) => f && f.toLowerCase() === 'lengthorwidthorheight');
                const hasTtBizType = factors.some((f: string) => f && f.toLowerCase() === 'ttbiztype');
                const hasFuelSurchargeRate = factors.some((f: string) => f && f.toLowerCase() === 'fuelsurchargerate');
                
                // 将 allPriceConfig 转换为 cards 数组（已按 groupId 排序）
                const cards = priceConfig.allPriceConfig.map((allPriceConfig: any) => {
                    const card: any = {};
                    
                    // 解析主因子配置
                    if (allPriceConfig.primaryFactorConfig) {
                        if (hasZone) {
                            // zone 可能是逗号分隔的多个值，需要将 factor_value 转换为 factor_name 用于显示
                            card.zones = allPriceConfig.primaryFactorConfig.split(',').map((z: string) => {
                                const trimmed = z.trim();
                                return factorValueToNameMap.get(trimmed) || trimmed;
                            }).filter((z: string) => z);
                        } else if (hasLine) {
                            // line 可能是逗号分隔的多个值，需要将 factor_value 转换为 factor_name 用于显示
                            card.lines = allPriceConfig.primaryFactorConfig.split(',').map((l: string) => {
                                const trimmed = l.trim();
                                return factorValueToNameMap.get(trimmed) || trimmed;
                            }).filter((l: string) => l);
                        } else if (hasTtBizType) {
                            // bizType（CBT/FBT）可能是逗号分隔的多个值，需要将 factor_value 转换为 factor_name 用于显示
                            card.bizTypes = allPriceConfig.primaryFactorConfig.split(',').map((b: string) => {
                                const trimmed = b.trim();
                                return factorValueToNameMap.get(trimmed) || trimmed;
                            }).filter((b: string) => b);
                        }
                    }
                    
                    // 解析内容配置JSON
                    if (allPriceConfig.contentPriceConfig) {
                        try {
                            const contentList = JSON.parse(allPriceConfig.contentPriceConfig);
                            if (Array.isArray(contentList) && contentList.length > 0) {
                                if (hasWeight) {
                                    // weightRows 格式
                                    card.weightRows = contentList.map((item: any) => ({
                                        startWeight: item.startWeight ?? '',
                                        endWeight: item.endWeight ?? '',
                                        weightUnit: item.weightUnit || 'lb',
                                        priceUsd: item.priceUsd ?? ''
                                    }));
                                } else if (hasWeightOrVolumeWeight) {
                                    // weightOrVolumeWeight 格式 - 每个 card 对应一个配置项
                                    const firstItem = contentList[0];
                                    if (firstItem) {
                                        card.weightOrVolumeWeight = firstItem.weightOrVolumeWeight ?? null;
                                        card.weightOrVolumeWeightUnit = firstItem.weightUnit || firstItem.weightOrVolumeWeightUnit || 'lb';
                                        card.priceUsd = firstItem.priceUsd ?? null;
                                    }
                                } else if (hasLengthOrWidthOrHeight) {
                                    // lengthOrWidthOrHeight 格式 - 每个 card 对应一个配置项
                                    const firstItem = contentList[0];
                                    if (firstItem) {
                                        card.lengthOrWidthOrHeight = firstItem.lengthOrWidthOrHeight ?? null;
                                        card.lengthOrWidthOrHeightUnit = firstItem.lengthUnit || firstItem.lengthOrWidthOrHeightUnit || 'inch';
                                        card.priceUsd = firstItem.priceUsd ?? null;
                                    }
                                } else if (hasTtBizType) {
                                    // bizType（CBT/FBT）格式 - 每个 card 对应一个配置项，只有价格没有重量
                                    const firstItem = contentList[0];
                                    if (firstItem) {
                                        card.priceUsd = firstItem.priceUsd ?? null;
                                    }
                                } else if (hasFuelSurchargeRate) {
                                    // 燃油附加费 - 只有一个费率（%）
                                    const firstItem = contentList[0];
                                    if (firstItem) {
                                        card.fuelSurchargeRate = firstItem.fuelSurchargeRate ?? null;
                                    }
                                }
                            }
                        } catch (e) {
                            console.error(`[CreateSchemeComp] 解析 contentPriceConfig 失败:`, e);
                        }
                    }
                    
                    return card;
                });
                
                newFeeItemDataMap.set(feeItemCodeNum, { cards });
                selectedFeeItemCodesSet.add(feeItemCodeNum);
            });
            
            setFeeItemDataMap(newFeeItemDataMap);
            setSelectedFeeItemCodes(selectedFeeItemCodesSet);
        }
    }, [schemeDetailData, viewMode, copyFromSchemeId, feeItems, feeItemFactorsMap, factorValueToNameMap]);

    // 当计费因子加载完成后，检查并修正数据结构
    useEffect(() => {
        if (feeItems && feeItems.length > 0 && feeItemFactorsMap.size > 0 && viewMode === 'create' && pricingFactorOptionsMap.size > 0) {
            setFeeItemDataMap(prev => {
                const newMap = new Map(prev);
                let hasChanges = false;

                // 获取默认选项
                const zoneOptions = pricingFactorOptionsMap.get('zone') || [];
                const lineOptions = pricingFactorOptionsMap.get('line') || [];
                const defaultZone = zoneOptions.length > 0 ? zoneOptions[0].value : '';
                const defaultLine = lineOptions.length > 0 ? lineOptions[0].value : '';

                feeItems.forEach((feeItem: any) => {
                    const feeItemCode = feeItem.businessFeeCode;
                    // 确保 feeItemCode 是数字类型，用于 Map 查找
                    const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
                    const factors = feeItemFactorsMap.get(feeItemCodeNum) || [];
                    const existingData = newMap.get(feeItemCodeNum);

                    if (factors.length > 0) {
                        const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
                        const hasLine = factors.some((f: string) => f && f.toLowerCase() === 'line');
                        const hasStartWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight');
                        const hasEndWeight = factors.some((f: string) => f && f.toLowerCase() === 'endweight');
                        const hasWeight = hasStartWeight && hasEndWeight;
                        const hasWeightOrVolumeWeight = factors.some((f: string) => f && f.toLowerCase() === 'weightorvolumeweight');
                        const hasLengthOrWidthOrHeight = factors.some((f: string) => f && f.toLowerCase() === 'lengthorwidthorheight');
                        const hasTtBizType = factors.some((f: string) => f && f.toLowerCase() === 'ttbiztype');
                        const hasFuelSurchargeRate = factors.some((f: string) => f && f.toLowerCase() === 'fuelsurchargerate');

                        if (existingData) {
                            const firstCard = existingData.cards?.[0];
                            const hasZonesInData = firstCard?.zones !== undefined;
                            const hasLinesInData = firstCard?.lines !== undefined;
                            const hasWeightRowsInData = firstCard?.weightRows !== undefined;

                            // 如果数据结构不匹配，重新初始化
                            const needsReinit = (hasZone && !hasZonesInData) || 
                                             (hasLine && !hasLinesInData && !hasZone) ||
                                             (hasWeight && !hasWeightRowsInData && !hasZone && !hasLine) ||
                                             (!hasWeight && hasWeightRowsInData && !hasZone && !hasLine);
                            
                            if (needsReinit) {
                                if (hasZone && hasWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ zones: [], weightRows: [{weightUnit: 'lb'}] }] });
                                    hasChanges = true;
                                } else if (hasZone && !hasWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ zones: [] }] });
                                    hasChanges = true;
                                } else if (hasLine && hasWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ lines: [], weightRows: [{weightUnit: 'lb'}] }] });
                                    hasChanges = true;
                                } else if (hasLine && !hasWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ lines: [] }] });
                                    hasChanges = true;
                                } else if (hasWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ weightRows: [{weightUnit: 'lb'}] }] });
                                    hasChanges = true;
                                } else if (hasWeightOrVolumeWeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ weightOrVolumeWeight: null, weightOrVolumeWeightUnit: 'lb', priceUsd: null }] });
                                    hasChanges = true;
                                } else if (hasLengthOrWidthOrHeight) {
                                    newMap.set(feeItemCodeNum, { cards: [{ lengthOrWidthOrHeight: null, lengthOrWidthOrHeightUnit: 'inch', priceUsd: null }] });
                                    hasChanges = true;
                                } else if (hasTtBizType) {
                                    newMap.set(feeItemCodeNum, { cards: [{ bizTypes: [], priceUsd: null }] });
                                    hasChanges = true;
                                } else if (hasFuelSurchargeRate) {
                                    newMap.set(feeItemCodeNum, { cards: [{ fuelSurchargeRate: null }] });
                                    hasChanges = true;
                                } else {
                                    newMap.set(feeItemCodeNum, { cards: [{}] });
                                    hasChanges = true;
                                }
                            }
                        } else {
                            // 如果计费因子已加载但没有数据，初始化数据
                            if (hasZone && hasWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ zones: [], weightRows: [{weightUnit: 'lb'}] }] });
                                hasChanges = true;
                            } else if (hasZone && !hasWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ zones: [] }] });
                                hasChanges = true;
                            } else if (hasLine && hasWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ lines: [], weightRows: [{weightUnit: 'lb'}] }] });
                                hasChanges = true;
                            } else if (hasLine && !hasWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ lines: [] }] });
                                hasChanges = true;
                            } else if (hasWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ weightRows: [{weightUnit: 'lb'}] }] });
                                hasChanges = true;
                            } else if (hasWeightOrVolumeWeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ weightOrVolumeWeight: null, weightOrVolumeWeightUnit: 'lb', priceUsd: null }] });
                                hasChanges = true;
                            } else if (hasLengthOrWidthOrHeight) {
                                newMap.set(feeItemCodeNum, { cards: [{ lengthOrWidthOrHeight: null, lengthOrWidthOrHeightUnit: 'inch', priceUsd: null }] });
                                hasChanges = true;
                            } else if (hasTtBizType) {
                                newMap.set(feeItemCodeNum, { cards: [{ bizTypes: [], priceUsd: null }] });
                                hasChanges = true;
                            } else if (hasFuelSurchargeRate) {
                                newMap.set(feeItemCodeNum, { cards: [{ fuelSurchargeRate: null }] });
                                hasChanges = true;
                            } else {
                                newMap.set(feeItemCodeNum, { cards: [{}] });
                                hasChanges = true;
                            }
                        }
                    }
                });

                return hasChanges ? newMap : prev;
            });
        }
    }, [feeItems, feeItemFactorsMap, factorsUpdateKey, pricingFactorOptionsMap, viewMode]);

    // 根据费用项动态生成费用项列表（不依赖 feeItemDataMap，避免单条编辑时整表重建；数据由各 Tab 从 PricingFormContext 读取）
    // 注意：这里用 visibleFeeItems（"费用项范围"二级选择框筛选后的子集）而不是全量 feeItems，
    // 仅影响页面上展示哪些Tab；已选中/已配置但当前被筛选隐藏的费用项，选中状态和数据都还在，
    // 保存时仍会按 feeItems 全量遍历提交，不会因为切换筛选框而丢失。
    const feeItemList = useMemo(() => {
        if (!visibleFeeItems || visibleFeeItems.length === 0) return [];

        return visibleFeeItems.map((feeItem: any) => {
            const feeItemCode = feeItem.businessFeeCode;
            const feeItemId = feeItem.id;
            const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
            const feeItemName = getFeeItemDisplayName(feeItem);
            const factors = feeItemFactorsMap.get(feeItemCodeNum) || [];

            const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
            const hasLine = factors.some((f: string) => f && f.toLowerCase() === 'line');
            const hasStartWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight');
            const hasEndWeight = factors.some((f: string) => f && f.toLowerCase() === 'endweight');
            const hasWeight = hasStartWeight && hasEndWeight;
            const hasWeightOrVolumeWeight = factors.some((f: string) => f && f.toLowerCase() === 'weightorvolumeweight');
            const hasLengthOrWidthOrHeight = factors.some((f: string) => f && f.toLowerCase() === 'lengthorwidthorheight');
            const hasTtBizType = factors.some((f: string) => f && f.toLowerCase() === 'ttbiztype');
            const hasFuelSurchargeRate = factors.some((f: string) => f && f.toLowerCase() === 'fuelsurchargerate');

            if (factors.length === 0 && queriedFeeItemCodesRef.current.has(feeItemCodeNum)) {
                return {
                    label: feeItemName,
                    key: feeItemId || feeItemCode.toString(),
                    feeItemCode: feeItemCode,
                    children: <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>该费用项无需配置</div>
                };
            }

            const zoneOptions = pricingFactorOptionsMap.get('zone') || [];
            const lineOptions = pricingFactorOptionsMap.get('line') || [];
            const bizTypeOptions = pricingFactorOptionsMap.get('ttBizType') || [];

            let children: React.ReactNode;
            if (hasZone && hasWeight) {
                children = <DeliveryFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} zoneOptions={zoneOptions} />;
            } else if (hasZone && !hasWeight) {
                children = <DeliveryFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} showWeight={false} disabled={isViewMode} zoneOptions={zoneOptions} />;
            } else if (hasLine && hasWeight) {
                children = <RouteFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} lineOptions={lineOptions} />;
            } else if (hasLine && !hasWeight) {
                children = <RouteFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} showWeight={false} disabled={isViewMode} lineOptions={lineOptions} />;
            } else if (hasWeight) {
                children = <PickupFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} />;
            } else if (hasWeightOrVolumeWeight) {
                children = <WeightOrVolumeWeightTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} />;
            } else if (hasLengthOrWidthOrHeight) {
                children = <LengthOrWidthOrHeightTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} />;
            } else if (hasTtBizType) {
                children = <BizTypeFeeTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} bizTypeOptions={bizTypeOptions} />;
            } else if (hasFuelSurchargeRate) {
                children = <FuelSurchargeRateTabb tabId={feeItemId} feeItemCodeNum={feeItemCodeNum} onDataChange={updateFeeItemDataByCode} disabled={isViewMode} />;
            } else {
                children = <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>该费用项无需配置</div>;
            }

            const isSelected = selectedFeeItemCodes.has(feeItemCodeNum);
            const handleCheckboxChange = (e: any) => {
                const checked = e.target.checked;
                setSelectedFeeItemCodes(prev => {
                    const newSet = new Set(prev);
                    if (checked) newSet.add(feeItemCodeNum);
                    else newSet.delete(feeItemCodeNum);
                    return newSet;
                });
            };

            return {
                label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox checked={isSelected} onChange={handleCheckboxChange} onClick={(e) => e.stopPropagation()} disabled={isViewMode} />
                        {feeItemName}
                    </span>
                ),
                key: feeItemId || feeItemCode.toString(),
                feeItemCode: feeItemCode,
                children
            };
        });
    }, [visibleFeeItems, feeItemFactorsMap, factorsUpdateKey, selectedFeeItemCodes, pricingFactorOptionsMap, isViewMode, updateFeeItemDataByCode]);

    const DELIVERY_FEE_ITEM_CODE = 1001001001;

    // 下载模板（配送费）
    const handleDownloadDeliveryTemplate = async () => {
        try {
            // 从 public 目录加载模板文件
            const response = await fetch('/配送费-配置定价方案模板.xlsx');
            if (!response.ok) {
                message.error('模板文件不存在');
                return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = '配送费-配置定价方案模板.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('配送费-配置定价方案模板下载成功');
        } catch (error) {
            console.error('配送费-配置定价方案模板模板失败:', error);
            message.error('配送费-配置定价方案模板模板失败');
        }
    };

    // 下载模板（退件费 TT）
    const handleDownloadTtTemplate = async () => {
        try {
            const response = await fetch('/TT退件费-配置定价方案模版.xlsx');
            if (!response.ok) {
                message.error('TT模板文件不存在');
                return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'TT退件费-配置定价方案模版.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('TT退件费-配置定价方案模版下载成功');
        } catch (error) {
            console.error('TT退件费-配置定价方案模版下载失败:', error);
            message.error('TT退件费-配置定价方案模版下载失败');
        }
    };

    // 上传模板并解析
    const handleTemplateUpload = async (file: File): Promise<boolean> => {
        let hideLoading: (() => void) | undefined;
        try {
            // 验证文件类型
            if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                message.error('请上传Excel文件（.xlsx, .xls）！');
                return false;
            }

            hideLoading = message.loading('正在解析模板并填充页面数据，请稍候...', 0);

            // 读取文件
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // 转换为 JSON 格式（数组的数组）
            const data: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

            const headerRow = data[0] || [];
            const dataRowCount = Math.max(0, data.length - 1); // 不包含表头行

            // 通过数据行数判断模板类型：TT 模板 AB列为 68 行
            if (dataRowCount === 68) {
                const ttValidationResult = validateTtTemplateFormat(data);
                if (!ttValidationResult.valid) {
                    hideLoading?.();
                    message.error(ttValidationResult.error || 'TT退件费模板格式校验失败');
                    return false;
                }
                const parsedData = parseTtTemplateData(data, ttValidationResult.zoneColumns || []);
                fillDataToPage(parsedData, ttValidationResult.zoneColumns || [], headerRow, TT_RETURN_FEE_CODE);
            } else {
                // 默认按配送费模板解析
                const validationResult = validateTemplateFormat(data);
                if (!validationResult.valid) {
                    hideLoading?.();
                    message.error(validationResult.error || '模板格式校验失败');
                    return false;
                }
                const parsedData = parseTemplateData(data, validationResult.zoneColumns || []);
                fillDataToPage(parsedData, validationResult.zoneColumns || [], headerRow, DELIVERY_FEE_ITEM_CODE);
            }

            hideLoading?.();
            // 显示成功提示
            Modal.success({
                title: '填充成功',
                content: '填充成功，请检核数据',
                okText: '确定'
            });

            return false; // 阻止自动上传
        } catch (error) {
            // 解析失败时关闭 loading，避免遮住错误提示
            hideLoading?.();
            console.error('解析模板失败:', error);
            message.error('解析模板失败：' + (error instanceof Error ? error.message : '未知错误'));
            return false;
        }
    };

    // Zone 列若整列无数据（如可选 Zone3-AUS），跳过该行级校验
    const isZoneColumnDataEmpty = (data: any[][], colIndex: number, startRow: number, endRow: number): boolean => {
        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
            const cellValue = data[rowIndex]?.[colIndex];
            if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
                return false;
            }
        }
        return true;
    };

    // 校验模板格式（配送费）
    const validateTemplateFormat = (data: any[][]): { valid: boolean; error?: string; zoneColumns?: number[] } => {
        // 1. 检查行数：至少需要旧模板结构（1行表头 + 47行数据）
        if (data.length < 48) {
            return { valid: false, error: '模板格式错误：数据行数不足48行' };
        }

        // 2. 检查 AB 列：跳过表头后，所有数据行 A/B 都必须存在
        const dataRows = data.slice(1);
        for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            if (row[0] === undefined || row[1] === undefined) {
                return { valid: false, error: `模板格式错误：第${i + 2}行的A列或B列为空，AB列不允许修改` };
            }
        }

        // 3. 从C列开始，查找zone列（表头行）
        const headerRow = data[0] || [];
        const zoneColumns: number[] = [];
        const zoneSet = new Set<string>();

        // 获取zone选项（用于校验）
        const zoneOptions = pricingFactorOptionsMap.get('zone') || [];
        const validZoneNames = new Set(zoneOptions.map(opt => opt.value));

        // 从C列（索引2）开始检查
        for (let colIndex = 2; colIndex < headerRow.length; colIndex++) {
            const headerValue = String(headerRow[colIndex] || '').trim();
            if (headerValue) {
                // 检查是否是有效的zone
                if (!validZoneNames.has(headerValue)) {
                    return { valid: false, error: `模板格式错误：第${colIndex + 1}列（${headerValue}）不是有效的zone区基础数据` };
                }
                // 检查是否重复
                if (zoneSet.has(headerValue)) {
                    return { valid: false, error: `模板格式错误：zone区数据重复（${headerValue}）` };
                }
                zoneSet.add(headerValue);
                zoneColumns.push(colIndex);
            }
        }

        if (zoneColumns.length === 0) {
            return { valid: false, error: '模板格式错误：未找到有效的zone列（从C列开始）' };
        }

        // 4. 检查数据区域：有数据的 zone 列不可为空，必须为数值，支持小数点后2位
        for (let rowIndex = 1; rowIndex < data.length; rowIndex++) { // 跳过表头，检查全部数据行
            const row = data[rowIndex] || [];
            for (const colIndex of zoneColumns) {
                if (isZoneColumnDataEmpty(data, colIndex, 1, data.length - 1)) {
                    continue;
                }
                const cellValue = row[colIndex];
                if (cellValue === undefined || cellValue === null || cellValue === '') {
                    return { valid: false, error: `模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）数据为空` };
                }
                // 检查是否为数值
                const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
                if (isNaN(numValue)) {
                    return { valid: false, error: `模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）不是有效的数值` };
                }
                // 检查小数位数
                const strValue = String(cellValue);
                const decimalIndex = strValue.indexOf('.');
                if (decimalIndex !== -1) {
                    const decimalPart = strValue.substring(decimalIndex + 1);
                    if (decimalPart.length > 2) {
                        return { valid: false, error: `模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）小数位数超过2位` };
                    }
                }
            }
        }

        return { valid: true, zoneColumns };
    };

    // 校验模板格式（退件费 TT）
    // - 左侧AB列：必须为68行，不许为空
    // - 从C列开始：zone 表头必须与基础数据匹配，且不能重复
    // - 数据区域：不可为空，必须为数值，支持小数点后2位
    const validateTtTemplateFormat = (data: any[][]): { valid: boolean; error?: string; zoneColumns?: number[] } => {
        const expectedDataRows = 68;
        const expectedTotalRows = expectedDataRows + 1; // +1 表头

        if (data.length < expectedTotalRows) {
            return { valid: false, error: `TT退件费模板格式错误：数据行数不足${expectedTotalRows}行（AB列需${expectedDataRows}行）` };
        }

        // 超出期望行数时，如果多出来的AB列有内容，则认为模板被修改
        if (data.length > expectedTotalRows) {
            const extraRows = data.slice(expectedTotalRows);
            const hasExtraABContent = extraRows.some((r) => (r?.[0] ?? '') !== '' || (r?.[1] ?? '') !== '');
            if (hasExtraABContent) {
                return { valid: false, error: `TT退件费模板格式错误：AB列必须为${expectedDataRows}行，检测到多余数据行` };
            }
        }

        const dataRows = data.slice(1, expectedTotalRows); // 取前68行数据
        for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            if (row[0] === undefined || row[0] === '' || row[1] === undefined || row[1] === '') {
                return { valid: false, error: `TT退件费模板格式错误：第${i + 2}行的A列或B列为空，AB列不允许修改/为空` };
            }
        }

        // 2. 从C列开始，查找zone列（表头行）
        const headerRow = data[0] || [];
        const zoneColumns: number[] = [];
        const zoneSet = new Set<string>();

        // 获取zone选项（用于校验）
        const zoneOptions = pricingFactorOptionsMap.get('zone') || [];
        const validZoneNames = new Set(zoneOptions.map(opt => opt.value));

        // 从C列（索引2）开始检查
        for (let colIndex = 2; colIndex < headerRow.length; colIndex++) {
            const headerValue = String(headerRow[colIndex] || '').trim();
            if (headerValue) {
                if (!validZoneNames.has(headerValue)) {
                    return { valid: false, error: `TT退件费模板格式错误：第${colIndex + 1}列（${headerValue}）不是有效的zone区基础数据` };
                }
                if (zoneSet.has(headerValue)) {
                    return { valid: false, error: `TT退件费模板格式错误：zone区数据重复（${headerValue}）` };
                }
                zoneSet.add(headerValue);
                zoneColumns.push(colIndex);
            }
        }

        if (zoneColumns.length === 0) {
            return { valid: false, error: 'TT退件费模板格式错误：未找到有效的zone列（从C列开始）' };
        }

        // 3. 检查数据区域：有数据的 zone 列不可为空，必须为数值，支持小数点后2位
        for (let rowIndex = 1; rowIndex <= expectedDataRows; rowIndex++) {
            const row = data[rowIndex] || [];
            for (const colIndex of zoneColumns) {
                if (isZoneColumnDataEmpty(data, colIndex, 1, expectedDataRows)) {
                    continue;
                }
                const cellValue = row[colIndex];
                if (cellValue === undefined || cellValue === null || cellValue === '') {
                    return {
                        valid: false,
                        error: `TT退件费模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）数据为空`
                    };
                }

                const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
                if (isNaN(numValue)) {
                    return {
                        valid: false,
                        error: `TT退件费模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）不是有效的数值`
                    };
                }

                const strValue = String(cellValue);
                const decimalIndex = strValue.indexOf('.');
                if (decimalIndex !== -1) {
                    const decimalPart = strValue.substring(decimalIndex + 1);
                    if (decimalPart.length > 2) {
                        return {
                            valid: false,
                            error: `TT退件费模板格式错误：第${rowIndex + 1}行第${colIndex + 1}列（${headerRow[colIndex]}）小数位数超过2位`
                        };
                    }
                }
            }
        }

        return { valid: true, zoneColumns };
    };

    // 解析模板数据
    const parseTemplateData = (data: any[][], zoneColumns: number[]): { zoneDataMap: Map<string, any[]>, weightRows: any[] } => {
        const zoneDataMap = new Map<string, any[]>();
        const headerRow = data[0] || [];
        const weightRows: any[] = [];

        // 提取所有数据行，构建 weightRows（A列是起始重量，B列是结束重量）
        // 第2行到第18行（索引1到17）仍为 OZ 单位，其余为 LB 单位
        for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
            const row = data[rowIndex] || [];
            const startWeight = typeof row[0] === 'number' ? row[0] : parseFloat(String(row[0] || 0));
            const endWeight = typeof row[1] === 'number' ? row[1] : parseFloat(String(row[1] || 0));

            // 第2行到第18行（索引1到17）使用 oz 单位，其他使用 lb 单位
            const weightUnit = (rowIndex >= 1 && rowIndex <= 17) ? 'oz' : 'lb';

            weightRows.push({
                startWeight: isNaN(startWeight) ? null : startWeight,
                endWeight: isNaN(endWeight) ? null : endWeight,
                weightUnit: weightUnit
            });

            // 为每个zone提取价格数据
            for (const colIndex of zoneColumns) {
                const zoneName = String(headerRow[colIndex] || '').trim();
                const cellValue = row[colIndex];
                const priceUsd = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));

                if (!zoneDataMap.has(zoneName)) {
                    zoneDataMap.set(zoneName, []);
                }
                zoneDataMap.get(zoneName)!.push({
                    rowIndex: rowIndex - 1,
                    priceUsd: isNaN(priceUsd) ? null : priceUsd
                });
            }
        }

        return { zoneDataMap, weightRows };
    };

    // 解析 TT 模板数据
    const parseTtTemplateData = (data: any[][], zoneColumns: number[]): { zoneDataMap: Map<string, any[]>, weightRows: any[] } => {
        const zoneDataMap = new Map<string, any[]>();
        const headerRow = data[0] || [];
        const weightRows: any[] = [];

        // TT 模板：AB 列固定为 68 行（不包括表头）
        const expectedDataRows = 68;

        for (let rowIndex = 1; rowIndex <= expectedDataRows; rowIndex++) {
            const row = data[rowIndex] || [];
            const rawStart = String(row[0] ?? '');
            const rawEnd = String(row[1] ?? '');
            const startWeight = typeof row[0] === 'number' ? row[0] : parseFloat(String(row[0] || 0));
            const endWeight = typeof row[1] === 'number' ? row[1] : parseFloat(String(row[1] || 0));

            // 从 A/B 单元格内容推断权重单位：OZ 或 LB
            // 这样即使 OZ/LB 分界行数调整，解析也不会错位
            const unitUpper = `${rawStart} ${rawEnd}`.toUpperCase();
            const weightUnit = unitUpper.includes('OZ') ? 'oz' : 'lb';

            weightRows.push({
                startWeight: isNaN(startWeight) ? null : startWeight,
                endWeight: isNaN(endWeight) ? null : endWeight,
                weightUnit: weightUnit
            });

            for (const colIndex of zoneColumns) {
                const zoneName = String(headerRow[colIndex] || '').trim();
                const cellValue = row[colIndex];
                const priceUsd = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));

                if (!zoneDataMap.has(zoneName)) {
                    zoneDataMap.set(zoneName, []);
                }
                zoneDataMap.get(zoneName)!.push({
                    rowIndex: rowIndex - 1,
                    priceUsd: isNaN(priceUsd) ? null : priceUsd
                });
            }
        }

        return { zoneDataMap, weightRows };
    };

    // 填充数据到页面
    const fillDataToPage = (
        parsedData: { zoneDataMap: Map<string, any[]>, weightRows: any[] },
        zoneColumns: number[],
        headerRow: any[],
        targetFeeItemCodeNum?: number
    ) => {
        // 获取zone选项
        const zoneOptions = pricingFactorOptionsMap.get('zone') || [];

        // 找到第一个有zone因子的费用项
        let targetFeeItemCode: number | null = null;

        // 优先使用期望的费用项code（用于配送费 vs TT 区分）
        if (targetFeeItemCodeNum != null) {
            const factors = feeItemFactorsMap.get(targetFeeItemCodeNum) || [];
            const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
            const hasWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight') &&
                factors.some((f: string) => f && f.toLowerCase() === 'endweight');
            if (hasZone && hasWeight) {
                targetFeeItemCode = targetFeeItemCodeNum;
            }
        }

        // 兜底：找任意一个 zone + weight 的费用项
        if (targetFeeItemCode == null) {
            for (const feeItem of feeItems) {
                const feeItemCode = feeItem.businessFeeCode;
                const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
                const factors = feeItemFactorsMap.get(feeItemCodeNum) || [];
                const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
                const hasWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight') &&
                    factors.some((f: string) => f && f.toLowerCase() === 'endweight');
                if (hasZone && hasWeight) {
                    targetFeeItemCode = feeItemCodeNum;
                    break;
                }
            }
        }

        if (!targetFeeItemCode) {
            message.warning('未找到支持zone和weight的费用项');
            return;
        }

        // 根据zone列创建卡片
        const newCards: any[] = [];

        for (const colIndex of zoneColumns) {
            const zoneName = String(headerRow[colIndex] || '').trim();
            const zonePriceData = parsedData.zoneDataMap.get(zoneName) || [];

            // 整列无价格数据时跳过（如未填写的可选 Zone）
            const hasAnyPrice = zonePriceData.some(
                (p) => p.priceUsd != null && !Number.isNaN(p.priceUsd)
            );
            if (!hasAnyPrice) {
                continue;
            }

            // 检查zone是否在选项中
            const zoneOption = zoneOptions.find(opt => opt.value === zoneName);
            if (!zoneOption) {
                continue;
            }

            // 将weightRows和价格数据合并
            const weightRowsWithPrice = parsedData.weightRows.map((weightRow, index) => {
                const priceData = zonePriceData.find(p => p.rowIndex === index);
                return {
                    ...weightRow,
                    priceUsd: priceData ? priceData.priceUsd : null
                };
            });

            newCards.push({
                zones: [zoneName],
                weightRows: weightRowsWithPrice
            });
        }

        // 更新费用项数据
        setFeeItemDataMap(prev => {
            const newMap = new Map(prev);
            newMap.set(targetFeeItemCode!, { cards: newCards });
            return newMap;
        });

        // 勾选费用项
        setSelectedFeeItemCodes(prev => {
            const newSet = new Set(prev);
            newSet.add(targetFeeItemCode!);
            return newSet;
        });
    };

    const handleSave = async () => {
        // 基础校验
        if (!clientId) {
            message.error('请选择客户');
            return;
        }
        if (!pricingSchemeName || pricingSchemeName.trim() === '') {
            message.error('请输入方案名称');
            return;
        }
        if (!businessScenario) {
            message.error('请选择业务场景');
            return;
        }

        // 收集所有有数据的费用项配置
        const priceConfig: any[] = [];
        let hasValidationError = false;
        const validationErrors: string[] = [];

        // 检查是否有选中的费用项
        if (selectedFeeItemCodes.size === 0) {
            message.error('请至少选择一个费用项进行配置');
            return;
        }

        // 遍历所有费用项，只处理被选中的费用项
        feeItems.forEach((feeItem: any) => {
            const feeItemCode = feeItem.businessFeeCode;
            // 确保 feeItemCode 是数字类型，用于 Map 查找
            const feeItemCodeNum = typeof feeItemCode === 'string' ? parseInt(feeItemCode, 10) : feeItemCode;
            const feeItemName = getFeeItemDisplayName(feeItem);
            
            // 只处理被选中的费用项
            if (!selectedFeeItemCodes.has(feeItemCodeNum)) {
                return;
            }
            
            const feeItemData = feeItemDataMap.get(feeItemCodeNum);
            const factors = feeItemFactorsMap.get(feeItemCodeNum) || [];

            if (!feeItemData) {
                validationErrors.push(`${feeItemName}：请配置费用项数据`);
                hasValidationError = true;
                return;
            }

            // 判断计费因子类型 - 使用精确匹配
            const hasZone = factors.some((f: string) => f && f.toLowerCase() === 'zone');
            const hasLine = factors.some((f: string) => f && f.toLowerCase() === 'line');
            const hasStartWeight = factors.some((f: string) => f && f.toLowerCase() === 'startweight');
            const hasEndWeight = factors.some((f: string) => f && f.toLowerCase() === 'endweight');
            const hasWeight = hasStartWeight && hasEndWeight;
            const hasWeightOrVolumeWeight = factors.some((f: string) => f && f.toLowerCase() === 'weightorvolumeweight');
            const hasLengthOrWidthOrHeight = factors.some((f: string) => f && f.toLowerCase() === 'lengthorwidthorheight');
            const hasTtBizType = factors.some((f: string) => f && f.toLowerCase() === 'ttbiztype');
            const hasFuelSurchargeRate = factors.some((f: string) => f && f.toLowerCase() === 'fuelsurchargerate');

            // 根据计费因子判断费用项类型并生成对应的价格配置
            if (hasZone) {
                // 配送费类型 - 有 zones
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    // 检查每个卡片是否有选中的zone和有效的weightRows
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (!c.zones || !Array.isArray(c.zones) || c.zones.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请选择Zone/分区`);
                            hasValidationError = true;
                        }
                        if (!c.weightRows || !Array.isArray(c.weightRows) || c.weightRows.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请至少添加一个重量行`);
                            hasValidationError = true;
                        } else {
                            for (let rowIndex = 0; rowIndex < c.weightRows.length; rowIndex++) {
                                const r = c.weightRows[rowIndex];
                                if (r.startWeight == null || r.startWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：起始重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.endWeight == null || r.endWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：结束重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (!r.weightUnit || r.weightUnit === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：单位不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.priceUsd == null || r.priceUsd === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：价格不能为空`);
                                    hasValidationError = true;
                                } else if (NEGATIVE_ONLY_FEE_ITEM_CODES.has(feeItemCodeNum) && Number(r.priceUsd) > 0) {
                                    // 负数费用项（TT预分拣费）价格只能小于等于0
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：价格不能大于0`);
                                    hasValidationError = true;
                                }
                            }
                        }
                    }
                }
                
                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .filter((card: any) => card.zones && Array.isArray(card.zones) && card.zones.length > 0 && card.weightRows && Array.isArray(card.weightRows))
                        .map((card: any, groupId: number) => {
                            // 将 zone 的 factor_name 转换为 factor_value
                            const zoneValues = card.zones.map((zoneName: string) => {
                                return factorNameToValueMap.get(zoneName) || zoneName;
                            });
                            return {
                                primaryFactorConfig: zoneValues.join(','), // 将所有选中的 zone value 用逗号连接
                                groupId,
                                contentPriceConfig: JSON.stringify(card.weightRows.map((r: any) => ({
                                    startWeight: r.startWeight,
                                    endWeight: r.endWeight,
                                    weightUnit: r.weightUnit,
                                    priceUsd: r.priceUsd
                                })))
                            };
                        });
            
                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            factorsType : 'zone',
                            allPriceConfig
                        });
                    }
                }
            } else if (hasLine) {
                // 线路费类型 - 有 lines
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    // 检查每个卡片是否有选中的line和有效的weightRows
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (!c.lines || !Array.isArray(c.lines) || c.lines.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请选择Line/线路`);
                            hasValidationError = true;
                        }
                        if (!c.weightRows || !Array.isArray(c.weightRows) || c.weightRows.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请至少添加一个重量行`);
                            hasValidationError = true;
                        } else {
                            for (let rowIndex = 0; rowIndex < c.weightRows.length; rowIndex++) {
                                const r = c.weightRows[rowIndex];
                                if (r.startWeight == null || r.startWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：起始重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.endWeight == null || r.endWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：结束重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (!r.weightUnit || r.weightUnit === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：单位不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.priceUsd == null || r.priceUsd === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：价格不能为空`);
                                    hasValidationError = true;
                                }
                            }
                        }
                    }
                }
                
                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .filter((card: any) => card.lines && Array.isArray(card.lines) && card.lines.length > 0 && card.weightRows && Array.isArray(card.weightRows))
                        .map((card: any, groupId: number) => {
                            // 将 line 的 factor_name 转换为 factor_value
                            const lineValues = card.lines.map((lineName: string) => {
                                return factorNameToValueMap.get(lineName) || lineName;
                            });
                            return {
                                primaryFactorConfig: lineValues.join(','), // 将所有选中的 line value 用逗号连接
                                groupId,
                                contentPriceConfig: JSON.stringify(card.weightRows.map((r: any) => ({
                                    startWeight: r.startWeight,
                                    endWeight: r.endWeight,
                                    weightUnit: r.weightUnit,
                                    priceUsd: r.priceUsd
                                })))
                            };
                        });
            
                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            factorsType : 'line',
                            allPriceConfig
                        });
                    }
                }
            } else if (hasWeight) {
                // 其他类型（取件费、分拣费等）- 只有 weightRows
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    // 检查每个卡片的weightRows
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (!c.weightRows || !Array.isArray(c.weightRows) || c.weightRows.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请至少添加一个重量行`);
                            hasValidationError = true;
                        } else {
                            for (let rowIndex = 0; rowIndex < c.weightRows.length; rowIndex++) {
                                const r = c.weightRows[rowIndex];
                                if (r.startWeight == null || r.startWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：起始重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.endWeight == null || r.endWeight === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：结束重量不能为空`);
                                    hasValidationError = true;
                                }
                                if (!r.weightUnit || r.weightUnit === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：单位不能为空`);
                                    hasValidationError = true;
                                }
                                if (r.priceUsd == null || r.priceUsd === '') {
                                    validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}，行${rowIndex + 1}）：价格不能为空`);
                                    hasValidationError = true;
                                }
                            }
                        }
                    }
                }
                
                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .filter((card: any) => card.weightRows && Array.isArray(card.weightRows))
                        .map((card: any, groupId: number) => {
                            return {
                                groupId,
                                contentPriceConfig: JSON.stringify(card.weightRows.map((r: any) => ({
                                    startWeight: r.startWeight,
                                    endWeight: r.endWeight,
                                    weightUnit: r.weightUnit,
                                    priceUsd: r.priceUsd
                                })))
                            };
                        });
            
                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            allPriceConfig
                        });
                    }
                }
            } else if (hasWeightOrVolumeWeight) {
                // Weight/Volume Weight 类型
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    // 检查每个卡片的所有字段
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (c.weightOrVolumeWeight == null || c.weightOrVolumeWeight === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：重量/体积重量不能为空`);
                            hasValidationError = true;
                        }
                        if (!c.weightOrVolumeWeightUnit || c.weightOrVolumeWeightUnit === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：单位不能为空`);
                            hasValidationError = true;
                        }
                        if (c.priceUsd == null || c.priceUsd === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：价格不能为空`);
                            hasValidationError = true;
                        }
                    }
                }
                
                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .map((card: any, groupId: number) => {
                            return {
                                groupId,
                                contentPriceConfig: JSON.stringify([{
                                    weightOrVolumeWeight: card.weightOrVolumeWeight,
                                    weightOrVolumeWeightUnit: card.weightOrVolumeWeightUnit,
                                    priceUsd: card.priceUsd
                                }])
                            };
                        });
            
                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            allPriceConfig
                        });
                    }
                }
            } else if (hasLengthOrWidthOrHeight) {
                // Length/Width/Height 类型
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    // 检查每个卡片的所有字段
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (c.lengthOrWidthOrHeight == null || c.lengthOrWidthOrHeight === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：长度/宽度/高度不能为空`);
                            hasValidationError = true;
                        }
                        if (!c.lengthOrWidthOrHeightUnit || c.lengthOrWidthOrHeightUnit === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：单位不能为空`);
                            hasValidationError = true;
                        }
                        if (c.priceUsd == null || c.priceUsd === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：价格不能为空`);
                            hasValidationError = true;
                        }
                    }
                }
                
                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .map((card: any, groupId: number) => {
                            return {
                                groupId,
                                contentPriceConfig: JSON.stringify([{
                                    lengthOrWidthOrHeight: card.lengthOrWidthOrHeight,
                                    lengthOrWidthOrHeightUnit: card.lengthOrWidthOrHeightUnit,
                                    priceUsd: card.priceUsd
                                }])
                            };
                        });
            
                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            allPriceConfig
                        });
                    }
                }
            } else if (hasTtBizType) {
                // 业务类型（TT：CBT/FBT）类型 - 取件费（TT）/ 卸车费（TT）
                // 验证所有卡片的所有字段都不为空（只收集错误，不阻止保存）
                if (!feeItemData.cards || feeItemData.cards.length === 0) {
                    validationErrors.push(`${feeItemName}：请至少添加一个配置卡片`);
                    hasValidationError = true;
                } else {
                    for (let cardIndex = 0; cardIndex < feeItemData.cards.length; cardIndex++) {
                        const c = feeItemData.cards[cardIndex];
                        if (!c.bizTypes || !Array.isArray(c.bizTypes) || c.bizTypes.length === 0) {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：请选择业务类型`);
                            hasValidationError = true;
                        }
                        if (c.priceUsd == null || c.priceUsd === '') {
                            validationErrors.push(`${feeItemName}（卡片${cardIndex + 1}）：价格不能为空`);
                            hasValidationError = true;
                        }
                    }
                }

                // 处理数据（使用原始数据，不进行过滤）
                if (feeItemData.cards && feeItemData.cards.length > 0) {
                    const allPriceConfig = feeItemData.cards
                        .filter((card: any) => card.bizTypes && Array.isArray(card.bizTypes) && card.bizTypes.length > 0)
                        .map((card: any, groupId: number) => {
                            // 将 bizType 的 factor_name 转换为 factor_value
                            const bizTypeValues = card.bizTypes.map((bizTypeName: string) => {
                                return factorNameToValueMap.get(bizTypeName) || bizTypeName;
                            });
                            return {
                                primaryFactorConfig: bizTypeValues.join(','),
                                groupId,
                                contentPriceConfig: JSON.stringify([{
                                    priceUsd: card.priceUsd
                                }])
                            };
                        });

                    if (allPriceConfig.length > 0) {
                        priceConfig.push({
                            pricingFeeItemCode: feeItemCode,
                            factorsType: 'ttBizType',
                            allPriceConfig
                        });
                    }
                }
            } else if (hasFuelSurchargeRate) {
                // 燃油附加费 - 只有一个费率（%），金额 = 基础运费 × 费率
                const rate = feeItemData.cards?.[0]?.fuelSurchargeRate;
                const rateError = validateFuelSurchargeRate(rate);
                if (rateError) {
                    validationErrors.push(`${feeItemName}：${rateError}`);
                    hasValidationError = true;
                } else {
                    // 主计费因子为燃油费率
                    priceConfig.push({
                        pricingFeeItemCode: feeItemCode,
                        factorsType: 'fuelSurchargeRate',
                        allPriceConfig: [{
                            primaryFactorConfig: null,
                            groupId: 0,
                            contentPriceConfig: JSON.stringify([{ fuelSurchargeRate: rate }])
                        }]
                    });
                }
            }
        });

        // 燃油附加费按基础运费计算，必须同时勾选基础运费（配送费 1001001001）
        if (selectedFeeItemCodes.has(FUEL_SURCHARGE_FEE_ITEM_CODE) && !selectedFeeItemCodes.has(BASE_PRICING_FEE_ITEM_CODE)) {
            validationErrors.push('配置燃油附加费时必须同时配置配送费（基础运费）');
            hasValidationError = true;
        }

        // 如果有验证错误，显示所有错误信息
        if (hasValidationError && validationErrors.length > 0) {
            // 显示所有错误信息
            const errorMessage = validationErrors.length === 1 
                ? validationErrors[0] 
                : `发现 ${validationErrors.length} 个错误：\n${validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')}`;
            message.error(errorMessage, 5); // 显示5秒，让用户有足够时间查看
            return;
        }

        if (priceConfig.length === 0) {
            message.error('请至少配置一个费用项');
            return;
        }

        const payload = {
            clientId,
            pricingSchemeId: viewMode === 'edit' ? pricingSchemeId : null,
            pricingSchemeName: pricingSchemeName.trim(),
            pricingSchemeDescription: pricingSchemeDescription.trim() || null,
            businessScenario,
            priceConfig
        };

        // 执行保存的函数
        const executeSave = async () => {
            try {
                if (viewMode === 'edit') {
                    // 编辑模式：调用更新接口
                    const { data, errors } = await updatePricingScheme({
                        variables: { input: payload },
                        fetchPolicy: 'network-only'
                    });
                    
                    if (errors && errors.length > 0) {
                        message.error(errors[0].message || '更新失败');
                        return;
                    }
                    
                    const res = data?.updatePricingSchemeByClientId;
                    if (res?.success) {
                        message.success(t('settlement.priceCenter.messages.updateSuccess'));
                        if (onSaveSuccess) {
                            onSaveSuccess();
                        }
                    } else {
                        message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.updateFailed')));
                    }
                } else {
                    // 新建模式：调用新建接口
                    const { data, errors } = await submitNewPricingScheme({
                        variables: { input: payload },
                        fetchPolicy: 'network-only' // 确保请求在 network 中可见
                    });
                    
                    if (errors && errors.length > 0) {
                        message.error(errors[0].message || '保存失败');
                        return;
                    }
                    
                    const res = data?.newPricingSchemeByClientId;
                    if (res?.success) {
                        message.success(t('settlement.priceCenter.messages.saveSuccess'));
                        // 保存成功后关闭抽屉并执行 Filter 操作
                        if (onSaveSuccess) {
                            onSaveSuccess();
                        }
                    } else {
                        message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.saveFailed')));
                    }
                }
            } catch (e: any) {
                message.error(e?.message || t('errors.INTERNAL_ERROR'));
            }
        };

        // 如果是编辑模式且方案已被绑定，弹出确认对话框
        if (viewMode === 'edit') {
            const bindCount = schemeDetailData?.getPricingSchemeDetail?.bindCount || 0;
            if (bindCount > 0) {
                Modal.confirm({
                    title: '确认更新',
                    content: '当前方案已被绑定，编辑后将影响绑定的方案，是否确认更新？',
                    okText: '确认',
                    cancelText: '取消',
                    onOk: executeSave
                });
                return;
            }
        }

        // 新建模式或未绑定的编辑模式，直接执行保存
        executeSave();
    };

    const formContextValue = useMemo(() => ({ feeItemDataMap, updateFeeItemDataByCode }), [feeItemDataMap, updateFeeItemDataByCode]);

    return (
        <PricingFormContext.Provider value={formContextValue}>
        <div>
            <Row justify="start"  gutter={[16, 8]}>
                <Col span={12} >
                    <ChannelSelectFilter
                        label="客户选择"
                        value={clientIdString}
                        onChange={handleClientChange}
                        onSelect={handleClientSelect}
                        spanClassName="w-[9em]"
                        required={true}
                        disabled={isViewMode}
                    />
                </Col>
                <Col span={12} >
                    <div style={{margin:5}}>
                        {clientId ? (
                            <p>
                                客户ID: {clientId}  客户名称: {clientName}
                            </p>
                        ) : (
                            <p>请选择客户</p>
                        )}
                    </div>
                </Col>
            </Row>
            <Divider />
            <Row justify="start"  gutter={[16, 8]}>
                <Col span={12} >
                    <InputFilterItem
                        label="方案名称"
                        value={pricingSchemeName}
                        onChange={(e) => setPricingSchemeName(e.target.value)}
                        spanClassName="w-[9em]"
                        disabled={isViewMode}
                        required={true}
                        placeholder=""
                    />
                </Col>
                <Col span={12} >
                   <InputFilterItem
                        label="方案备注"
                        value={pricingSchemeDescription}
                        onChange={(e) => setPricingSchemeDescription(e.target.value)}
                        disabled={isViewMode}
                        placeholder=""
                    />
                </Col>
            </Row>
            {(viewMode === 'create' || businessScenarioNum === BUSINESS_SCENARIO_ORDER) && (
                <Row justify="start" gutter={[16, 8]} style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <Col span={24}>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                            {viewMode === 'create' && (
                                <Button
                                    type="default"
                                    icon={<FileAddOutlined />}
                                    onClick={() => setTemplateSectionVisible(!templateSectionVisible)}
                                >
                                    通过模版上传配置定价方案
                                    <DownOutlined style={{ 
                                        marginLeft: '8px',
                                        transform: templateSectionVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s'
                                    }} />
                                </Button>
                            )}
                            {businessScenarioNum === BUSINESS_SCENARIO_ORDER && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>费用项范围：</span>
                                    <Select
                                        value={feeItemScenarioFilter}
                                        style={{ width: 220 }}
                                        options={FEE_ITEM_SCENARIO_FILTER_OPTIONS}
                                        onChange={setFeeItemScenarioFilter}
                                    />
                                </div>
                            )}
                        </div>
                        {viewMode === 'create' && templateSectionVisible && (
                            <div style={{ 
                                marginTop: '12px', 
                                padding: '16px', 
                                border: '1px solid #d9d9d9', 
                                borderRadius: '4px',
                                backgroundColor: '#fafafa'
                            }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={handleDownloadDeliveryTemplate}
                                    >
                                        配送费-定价方案模板下载
                                    </Button>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={handleDownloadTtTemplate}
                                    >
                                        TT退件费-定价方案模板下载
                                    </Button>
                                    <Upload
                                        accept=".xlsx,.xls"
                                        beforeUpload={handleTemplateUpload}
                                        showUploadList={false}
                                    >
                                        <Button icon={<UploadOutlined />}>
                                            上传定价配置文件（配送/TT）
                                        </Button>
                                    </Upload>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            )}
             <Row justify="center"  gutter={[8, 16]}>
                <Col >
                    <div style={{ marginTop: '16px', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        💡 只有✅该费用项才会被配置；
                        ⚠️ 重量和金额只支持到两位小数
                    </div>
                    <PricingItemPanel  
                        priceItemTabs={feeItemList}
                    />
                </Col>
             </Row>
             <Divider />
             <Row justify="end" style={{margin:25}}>
                <Col>
                    <Button type="default" size="large" style={{margin:15}} onClick={onSaveSuccess}>返回</Button>
                    {(viewMode === 'create' || viewMode === 'edit') && (
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                            <Button type="primary" size="large" style={{margin:15}} onClick={handleSave} loading={saving}>保存</Button>
                        </HasResource>
                    )}
                </Col>
             </Row>
        </div>
        </PricingFormContext.Provider>
    );
}

export default CreateSchemeComp;